import { useRef, useState, type ReactNode } from "react";
import { Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { ForwardDialog, type ForwardMessage } from "./ForwardDialog";
import type { PickerAgent } from "./MentionList";
import { latestForwardableMessage } from "./noteThreads";
import type { TicketMessage } from "./noteThreads";
import type { AgentsState } from "./recipients";
import { useLiveAnnouncement } from "./useLiveAnnouncement";

/** What the host reads of a thread row: what the dialog shows, and what decides the button. */
export type ForwardHostMessage = ForwardMessage &
  Pick<TicketMessage, "isNote" | "isForward" | "isInternal">;

export interface ForwardHostOptions {
  /** The thread's messages, oldest first. */
  messages: readonly ForwardHostMessage[];
  /** The tenant's colleagues (the `agents` query of the ticket). */
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** Without notes there is no "An Kollegen senden": no button, no dialog. */
  enableNotes?: boolean;
}

export interface ForwardHost {
  /** The "An Kollegen senden" button of the thread's foot, or null where there is nothing to share. */
  button: ReactNode;
  /** The one dialog of the ticket and the live region that confirms a send. */
  renderDialog: (conversationId: string) => ReactNode;
}

/**
 * The host side of "An Kollegen senden" (#717) for the ticket view: one labelled button at the
 * foot of the thread that shares the newest message `canForwardMessage` allows (inbound or
 * outbound mail, never a note, a send or an internal row), one dialog, focus back on the button
 * when it closes, and a polite confirmation after a send. Sharing with a colleague is not a
 * customer reply: it stays available without the editor (D-05), only where notes are enabled.
 */
export function useForwardHost({
  messages,
  agents,
  agentsState,
  enableNotes,
}: ForwardHostOptions): ForwardHost {
  const { t } = useTranslation();
  const [messageId, setMessageId] = useState<string | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Announced on every send, also a second one with the same count.
  const { announce, region } = useLiveAnnouncement();

  const latest = enableNotes ? latestForwardableMessage(messages) : undefined;
  const button = latest ? (
    <Button
      type="button"
      variant="default"
      size="md"
      leadingIcon={Share2}
      onClick={(event) => {
        returnFocusRef.current = event.currentTarget;
        setMessageId(latest.id);
      }}
    >
      {t("tickets.forward.trigger")}
    </Button>
  ) : null;

  const renderDialog = (conversationId: string) => {
    if (!enableNotes) return null;
    return (
      <>
        <ForwardDialog
          message={messages.find((m) => m.id === messageId) ?? null}
          conversationId={conversationId}
          open={messageId !== null}
          onOpenChange={(next) => {
            if (!next) setMessageId(null);
          }}
          agents={agents}
          agentsState={agentsState}
          onSent={(count) =>
            announce(t("tickets.forward.status.sent", { count }))
          }
          returnFocusRef={returnFocusRef}
        />
        {region}
      </>
    );
  };

  return { button, renderDialog };
}
